import Axios from "axios";
import type { AxiosInstance, AxiosResponse } from "axios";

import { StorageService } from "@libs/storage-service";

import type { AxiosApiResponse, AxiosApiError } from "./types";
const defaultConfig = {
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
    "Content-Security-Policy": "default-src 'self'",
    "X-Frame-Options": "DENY",
    "X-Content-Type-Options": "nosniff",
    "Strict-Transport-Security": "max-age=31536000; includeSubDomains; preload",
  },
};

export const apiClient = <T = unknown>(baseURL: string): AxiosInstance => {
  const axiosApiClient = Axios.create({
    baseURL,
    ...defaultConfig,
  });

  axiosApiClient.interceptors.request.use(
    (config) => {
      const token: string | null = StorageService.get("accessToken");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  const clearStorageAndRedirectToLogin = () => {
    if (window.location.pathname !== "/auth") {
      localStorage.clear();
      window.location.replace("/auth");
    }
  };

  const isConnectionError = (err: unknown): boolean => {
    if (err && typeof err === "object") {
      const code = (err as { code?: string }).code;
      const message = String((err as { message?: string }).message ?? "").toLowerCase();
      if (code === "ECONNREFUSED" || code === "ERR_NETWORK") return true;
      if (
        message.includes("connection refused") ||
        message.includes("unable to connect") ||
        message.includes("network error")
      )
        return true;
    }
    return false;
  };

  axiosApiClient.interceptors.response.use(
    (response: AxiosResponse<AxiosApiResponse<T>>) => response,
    (error: AxiosApiError | unknown) => {
      if (error && typeof error === "object" && "response" in error) {
        const { response } = error as AxiosApiError;
        const { status } = response || {};
        if (status === 401) {
          clearStorageAndRedirectToLogin();
        }
        return Promise.reject(response);
      }
      // Network/connection errors (no response): clear storage and redirect to login
      if (isConnectionError(error)) {
        clearStorageAndRedirectToLogin();
      }
      return Promise.reject(error);
    }
  );
  return axiosApiClient;
};
